import { BaseFormAction, BaseInput, BaseTextarea, Field, Radio, RadioGroup } from '@/components';
import { setBreadcrumbs, useLoadingOverlay, useUnsavedChanges } from '@/hooks';
import { trans } from '@/i18n';
import styles from '@/pages/account/styles/user-page.module.scss';
import createNoteYup from '@/pages/note/utils/createNote.validate';
import { ROUTE_PATH } from '@/routes';
import { NoteService } from '@/services/note';
import { INoteForm } from '@/types';
import { showError, showSuccess } from '@/utils';
import { Box, Flex, Grid } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

function NoteCreatePage() {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState<boolean>(true);
  const { toggleLoading } = useLoadingOverlay();
  const { setUnsavedChanges, resetChanges } = useUnsavedChanges();  
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
  } = useForm<INoteForm>({
    mode: 'onBlur',
    resolver: createNoteYup,
  });

  setBreadcrumbs(ROUTE_PATH.NOTE, [
    { name: trans('menu.note'), path: ROUTE_PATH.NOTE },
    { name: trans('menu.noteCreate'), subname: trans('menu.noteSubCreate') },
  ]);

  const sv = new NoteService();

  useEffect(() => {
    setUnsavedChanges(isDirty);

    return () => {
      resetChanges();
    };
  }, [isDirty, setUnsavedChanges]);

  const handleChangeIsActive = ({ value }: { value: string }) => {
    setIsActive(value == 'ACTIVE');
  };

  const trimNoteLink = (url: string) => {
    if (url.includes('note.com')) {
      return url.replace(/^https:\/\/note\.com\/([^/]+)\/?.*$/, 'https://note.com/$1');
    }
    const match = url.match(/^(https?:\/\/[^\/]+)/i);
    return match ? match[1] : '';
  }

  const onSubmit: SubmitHandler<INoteForm> = async (data) => {
    try {
      toggleLoading(true);
      const bodyNote: INoteForm = {
        name: data?.name?.trim(),
        link: trimNoteLink(data?.link?.trim()),
        memo: data?.memo?.trim() ?? "",
        isActive
      };

      console.log(bodyNote);
      await sv.storeData(bodyNote);
      showSuccess('note.createSuccess');
      navigate(ROUTE_PATH.NOTE);
    } catch (error) {
      showError(error);
      reset({...data, link: '' });
    } finally {
      toggleLoading(false);
    }
  };

  return (
    <Box width="full" alignContent="center">
      <div className="page-title">{trans('note.add')}</div>
      <div className="page-subtitle">{trans('note.addSubtitle')}</div>
      <Box className="box-create-content box-note-create" width="931px">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid gap={'17px'}>
            <Flex justifyContent="space-between">
              <BaseInput
                errors={errors}
                control={control}
                name="link"
                label={trans('note.link')}
                width="50%"
                span="auto"
                placeholder="例）https://note.com/sample/"
                isRequired
              />
            </Flex>
            <Flex justifyContent="space-between">
              <BaseInput
                errors={errors}
                control={control}
                name="name"
                label={trans('note.name')}
                width="50%"
                span="auto"
                placeholder="例）NINE"
                isRequired
              />
            </Flex>
            <Flex justifyContent="space-between">
              <BaseTextarea
                errors={errors}
                control={control}
                name="memo"
                label={trans('note.memo')}
                width="100%"
                span="auto"
                placeholder="必要に応じてメモを設定できます"
              />
            </Flex>

            <Flex justifyContent="space-between">
              <Field label={trans('note.status')}>
                <RadioGroup
                  value={isActive ? 'ACTIVE' : 'NOT_ACTIVE'}
                  onValueChange={(value: any) => handleChangeIsActive(value)}
                  colorPalette="green"
                  defaultValue="react"
                  size={'xs'}
                  className={styles.radioGroup}
                >
                  <Radio value="ACTIVE">表示</Radio>
                  <Radio value="NOT_ACTIVE">非表示</Radio>
                </RadioGroup>
              </Field>
              <BaseFormAction
                flex={1}
                justifyContent="flex-end"
                isAdd={true}
                path={ROUTE_PATH.NOTE}
                txtAdd="client.create"
              />
            </Flex>
          </Grid>
        </form>
      </Box>
    </Box>
  );
}

export default NoteCreatePage;
